import React, { useState, useEffect } from 'react';
import { MdAccountBalance } from 'react-icons/md';
import { Helmet } from 'react-helmet';
import DriverPage from './DriverPage';
import { useAuth } from '../../context/AuthContext';
import { fetchDriverEarnings } from '../../Services/ApiCallsServices/DriverDashboardApi';
import PulseLoader from 'react-spinners/PulseLoader';

const Earnings = () => {
  const [period, setPeriod] = useState('7');
  const [allTrips, setAllTrips] = useState([]);
  const [filteredTrips, setFilteredTrips] = useState([]);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const { auth } = useAuth();

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    const loadEarnings = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchDriverEarnings();
        setAllTrips(data.trips || []);
        applyFilter(data.trips || [], period);
      } catch (err) {
        console.error('Earnings fetch failed:', err);
        setError('Failed to load earnings. Please try later.');
      } finally {
        setIsLoading(false);
      }
    };

    loadEarnings();
  }, [auth.isAuthenticated, period]);

  useEffect(() => {
    if (allTrips.length > 0) applyFilter(allTrips, period);
  }, [period, allTrips]);

  const applyFilter = (trips, period) => {
    const days = parseInt(period);
    const now = new Date();

    const filtered = trips.filter(trip => {
      if (!trip.createdAt) return false;
      const tripDate = new Date(trip.createdAt);
      const diffDays = Math.ceil((now - tripDate) / (1000 * 60 * 60 * 24));
      return diffDays <= days;
    });

    setFilteredTrips(filtered);
    setTotalEarnings(filtered.reduce((sum, trip) => sum + (trip.amount || 0), 0));
  };

  const openModal = (trip) => {
    setSelectedTrip(trip);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedTrip(null);
    setIsModalOpen(false);
  };

  return (
    <DriverPage>
      <Helmet>
        <title>Earnings | Premium Dashboard</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center mb-6">
          <MdAccountBalance className="text-yellow-500 text-3xl mr-3" />
          <h1 className="text-2xl font-bold text-gray-800">Total Earnings</h1>
        </div>

        {/* Period Selector */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center">
            <label htmlFor="period" className="mr-3 text-gray-700 font-medium">
              Filter by:
            </label>
            <select
              id="period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              disabled={isLoading}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="1">Last 1 day</option>
              <option value="3">Last 3 days</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
            </select>
          </div>
        </div>

        {/* Loading/Error States */}
        {isLoading && (
          <div className="flex justify-center items-center h-64">
              <PulseLoader color="#FFD700" size={15} />
          </div>
        )}
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
            <p>{error}</p>
          </div>
        )}

        {/* Earnings Summary */}
        {!isLoading && !error && (
          <>
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-700 mb-2">
                Total Earnings: 
                <span className="text-yellow-500 ml-2 text-xl font-bold">
                  R{totalEarnings.toFixed(2)}
                </span>
              </h2>
            </div>
        
            {/* No Earnings State */}
            {filteredTrips.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-8 flex flex-col items-center justify-center mb-6">
                <MdAccountBalance className="text-yellow-300 text-5xl mb-4" />
                <p className="text-gray-500 text-lg font-medium mb-2">No earnings found for this period.</p>
                <p className="text-gray-400 text-sm">Try selecting a different period or check back later.</p>
              </div>
            ) : (
              // Trips Table
              <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
                <h2 className="text-lg font-semibold p-4 border-b border-gray-200">
                  Trip Details
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Reference
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Pickup Location
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Dropoff Location
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredTrips.map((trip, index) => (
                        <tr 
                          key={trip.id || index} 
                          onClick={() => openModal(trip)}
                          className="hover:bg-gray-50 cursor-pointer transition"
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {trip.reference || `Trip-${index + 1}`}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {trip.pickupLocation}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {trip.dropoffLocation}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            R{(trip.amount || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Trip Details Modal */}
        {isModalOpen && selectedTrip && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={closeModal}></div>
              </div>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-start">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Trip Details</h2>
                    <button 
                      onClick={closeModal}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">Close</span>
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="border-b pb-2">
                      <p className="text-sm font-medium text-gray-500">Reference:</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedTrip.reference}</p>
                    </div>
                    <div className="border-b pb-2">
                      <p className="text-sm font-medium text-gray-500">Status:</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedTrip.status}</p>
                    </div>
                    <div className="border-b pb-2">
                      <p className="text-sm font-medium text-gray-500">Pickup Location:</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedTrip.pickupLocation}</p>
                    </div>
                    <div className="border-b pb-2">
                      <p className="text-sm font-medium text-gray-500">Dropoff Location:</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedTrip.dropoffLocation}</p>
                    </div>
                    <div className="border-b pb-2">
                      <p className="text-sm font-medium text-gray-500">Customer Name:</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedTrip.customerName}</p>
                    </div>
                    <div className="border-b pb-2">
                      <p className="text-sm font-medium text-gray-500">Driver Name:</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedTrip.driverName}</p>
                    </div>
                    <div className="border-b pb-2">
                      <p className="text-sm font-medium text-gray-500">Amount:</p>
                      <p className="mt-1 text-sm font-bold text-yellow-500">
                        R{selectedTrip.amount.toFixed(2)}
                      </p>
                    </div>
                    <div className="border-b pb-2">
                      <p className="text-sm font-medium text-gray-500">Created At:</p>
                      <p className="mt-1 text-sm text-gray-900">{selectedTrip.createdAt}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Completed At:</p>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedTrip.completedAt || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow-500 text-base font-medium text-white hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DriverPage>
  );
};

export default Earnings;